import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Download, FileText, Loader2, MessageCircle, Pencil, RefreshCw, Send, ShoppingBag, XCircle,
} from "lucide-react";
import { api, apiError, API, inr2, logError, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { STATUS_COLOR } from "@/pages/Quotations";
import { DocumentJourney } from "@/components/DocumentJourney";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState("");
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [waResult, setWaResult] = useState(null);

  const load = useCallback(
    () =>
      api
        .get(`/quotations/${id}`)
        .then(({ data }) => setQ(data))
        .catch(() => setQ(false)),
    [id]
  );

  useEffect(() => {
    load();
    api
      .get(`/audit-trail?entity=quotation&entity_id=${id}`)
      .then(({ data }) => setHistory(data))
      .catch((e) => logError("quotation history", e));
  }, [load, id]);

  const act = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (q === null)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  if (q === false) return <Empty title="Quotation not found" />;

  const pdfUrl = `${API}/quotations/${id}/pdf`;

  return (
    <div data-testid="quotation-detail">
      <PageHeader
        title={q.number}
        subtitle={`${q.customer_name} • ${q.project_name || "No project"} • ${shortDate(q.date)}`}
        actions={
          <>
            <Button variant="outline" asChild className="gap-1.5">
              <Link to="/quotations">
                <ArrowLeft className="h-4 w-4" /> All
              </Link>
            </Button>
            <Button variant="outline" asChild data-testid="quotation-pdf-button" className="gap-1.5">
              <a href={pdfUrl} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4" /> PDF
              </a>
            </Button>
            {can("quotations", "edit") && !["Approved", "Converted"].includes(q.status) && (
              <Button variant="outline" asChild data-testid="quotation-edit-button" className="gap-1.5">
                <Link to={`/quotations/${id}/edit`}>
                  <Pencil className="h-4 w-4" /> Edit
                </Link>
              </Button>
            )}
            {q.status === "Draft" && can("quotations", "edit") && (
              <Button
                data-testid="quotation-submit-approval-button"
                disabled={busy === "submit"}
                onClick={() => act("submit", () => api.post(`/quotations/${id}/submit`))}
                className="gap-1.5"
              >
                <Send className="h-4 w-4" /> Send for approval
              </Button>
            )}
            {q.status === "Pending Approval" && can("quotations", "approve") && (
              <>
                <Button
                  data-testid="quotation-approve-button"
                  disabled={busy === "approve"}
                  onClick={() =>
                    act("approve", () => api.post(`/quotations/${id}/approve`, { decision: "approve" }))
                  }
                  className="gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </Button>
                <Button
                  variant="destructive"
                  data-testid="quotation-reject-button"
                  onClick={() =>
                    act("reject", () => api.post(`/quotations/${id}/approve`, { decision: "reject" }))
                  }
                  className="gap-1.5"
                >
                  <XCircle className="h-4 w-4" /> Reject
                </Button>
              </>
            )}
            {can("quotations", "view") && (
              <Button
                variant="outline"
                data-testid="quotation-whatsapp-button"
                disabled={busy === "wa"}
                onClick={() =>
                  act("wa", async () => {
                    const { data } = await api.post(`/quotations/${id}/share-whatsapp`);
                    setWaResult(data);
                    if (data.status === "sent") toast.success("Sent on WhatsApp");
                  })
                }
                className="gap-1.5"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </Button>
            )}
            {can("quotations", "create") && (
              <Button
                variant="outline"
                data-testid="quotation-revise-button"
                onClick={() => setReviseOpen(true)}
                className="gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                {["Approved", "Converted"].includes(q.status) ? "Revise (new version)" : "Revise"}
              </Button>
            )}
            {["Approved", "Accepted", "Sent"].includes(q.status) && can("sales_orders", "create") && (
              <Button
                data-testid="quotation-convert-button"
                disabled={busy === "convert"}
                onClick={() =>
                  act("convert", async () => {
                    const { data } = await api.post(`/quotations/${id}/convert`);
                    toast.success(`Order ${data.sales_order.number} and project ${data.project.code} created`);
                  })
                }
                className="gap-1.5"
              >
                <ShoppingBag className="h-4 w-4" /> Convert to order
              </Button>
            )}
            {["Approved", "Accepted", "Sent", "Converted"].includes(q.status) && can("invoices", "create") && (
              <>
                <Button
                  data-testid="quotation-convert-invoice-button"
                  disabled={busy === "invoice"}
                  onClick={() =>
                    act("invoice", async () => {
                      const { data } = await api.post(`/quotations/${id}/convert-invoice`,
                        { kind: "tax_invoice" });
                      toast.success(`Tax invoice ${data.number} created`);
                      navigate(`/invoices/${data.id}`);
                    })
                  }
                  className="gap-1.5"
                >
                  <FileText className="h-4 w-4" /> Convert to invoice
                </Button>
                <Button
                  variant="outline"
                  data-testid="quotation-convert-proforma-button"
                  disabled={busy === "proforma"}
                  onClick={() =>
                    act("proforma", async () => {
                      const { data } = await api.post(`/quotations/${id}/convert-invoice`,
                        { kind: "proforma" });
                      toast.success(`Proforma ${data.number} created`);
                      navigate(`/invoices/${data.id}`);
                    })
                  }
                  className="gap-1.5"
                >
                  <FileText className="h-4 w-4" /> Proforma
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 sm:px-6">
        <Badge variant={STATUS_COLOR[q.status] || "secondary"} data-testid="quotation-status-badge">
          {q.status}
        </Badge>
        {q.revision > 0 && <Badge variant="outline">Revision R{q.revision}</Badge>}
        {q.margin_warning && <Badge variant="destructive">Low margin</Badge>}
        {(q.approval_chain || []).map((s) => (
          <Badge key={`${s.role}-${s.status}-${s.at || "pending"}`} variant="outline" className="text-[10px]">
            {s.role.replace(/_/g, " ")}: {s.status}
            {s.by ? ` • ${s.by}` : ""}
          </Badge>
        ))}
      </div>

      {waResult && (
        <div
          data-testid="whatsapp-result"
          className="mx-4 mt-4 rounded-md border border-border bg-secondary/50 p-4 text-sm sm:mx-6"
        >
          <div className="mb-1 font-medium">
            WhatsApp {waResult.status === "sent" ? "sent" : "not configured"}
          </div>
          {waResult.detail && <p className="mb-2 text-xs text-muted-foreground">{waResult.detail}</p>}
          <pre className="whitespace-pre-wrap rounded bg-background p-3 text-xs">{waResult.message}</pre>
          {waResult.status !== "sent" && (
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="whatsapp-copy-button"
                onClick={() => {
                  navigator.clipboard?.writeText(waResult.message);
                  toast.success("Message copied");
                }}
              >
                Copy message
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a
                  href={`https://wa.me/${(waResult.to || "").replace(/\D/g, "")}?text=${encodeURIComponent(
                    waResult.message
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open WhatsApp manually
                </a>
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[1fr_320px]">
        <div className="lg:col-span-2">
          <DocumentJourney entity="quotation" id={id} compact />
        </div>
        <div className="space-y-4">
          <div className="surface overflow-x-auto" data-testid="quotation-items-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(q.items || []).map((it, i) => (
                  <TableRow key={`${it.product_id || it.name}-${i}`}>
                    <TableCell>
                      <div className="text-sm font-medium">{it.name}</div>
                      <div className="line-clamp-1 text-xs text-muted-foreground">{it.description}</div>
                      {it.photos?.length > 0 && (
                        <div className="mt-1.5 flex gap-1">
                          {it.photos.slice(0, 4).map((p) => (
                            <img
                              key={p}
                              src={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`}
                              alt=""
                              className="h-10 w-12 rounded border border-border bg-secondary object-contain"
                            />
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm">
                      {it.quantity} {it.unit}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm">{inr2(it.rate)}</TableCell>
                    <TableCell className="text-right font-mono-num text-sm">{inr2(it.taxable)}</TableCell>
                    <TableCell className="text-right font-mono-num text-sm">
                      {it.gst_rate}% / {inr2(it.tax_amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm">{inr2(it.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {(q.revisions || []).length > 1 && (
            <div className="surface p-5" data-testid="quotation-revisions">
              <div className="overline mb-3">Revision history</div>
              {q.revisions.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                  <Link to={`/quotations/${r.id}`} className="font-mono-num text-primary hover:underline">
                    {r.number}
                  </Link>
                  <span className="text-xs text-muted-foreground">{r.revision_reason || "Original"}</span>
                  <span className="font-mono-num text-xs">{inr2(r.grand_total)}</span>
                  {r.price_difference != null && r.revision > 0 && (
                    <span
                      className={`font-mono-num text-xs ${
                        Number(r.price_difference) >= 0 ? "text-emerald-600" : "text-destructive"
                      }`}
                    >
                      {Number(r.price_difference) >= 0 ? "+" : ""}
                      {inr2(r.price_difference)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="surface p-5" data-testid="quotation-change-history">
            <div className="overline mb-3">Change history</div>
            {history.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No changes recorded yet — every edit, status change and approval will appear here.
              </p>
            ) : (
              history.map((h) => (
                <div key={h.id} className="border-b border-border py-2 text-xs last:border-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium capitalize">
                      {String(h.action).replace(/_/g, " ")}
                    </span>
                    <span className="text-muted-foreground">
                      {h.user_name} • {String(h.created_at).slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-muted-foreground">
                    {h.old_value?.total != null && h.new_value?.total != null && (
                      <span className="font-mono-num">
                        {inr2(h.old_value.total)} → {inr2(h.new_value.total)}
                      </span>
                    )}
                    {h.new_value?.items != null && (
                      <span>
                        items {h.old_value?.items != null ? `${h.old_value.items} → ` : ""}
                        {h.new_value.items}
                      </span>
                    )}
                    {h.new_value?.status && (
                      <span>
                        status {h.old_value?.status ? `${h.old_value.status} → ` : ""}
                        {h.new_value.status}
                      </span>
                    )}
                    {h.new_value?.reason && <span>reason: {h.new_value.reason}</span>}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="surface p-5">
            <div className="overline mb-2">Terms</div>
            <pre className="whitespace-pre-wrap text-xs text-muted-foreground">{q.terms}</pre>
            <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
              <div>
                <span className="overline">Payment</span>
                <div>{q.payment_terms || "—"}</div>
              </div>
              <div>
                <span className="overline">Warranty</span>
                <div>{q.warranty || "—"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="surface p-5" data-testid="quotation-summary">
            {[
              ["Subtotal", q.subtotal],
              ["Discount", q.discount_total],
              ["Charges", q.charges_total],
              ["Taxable value", q.taxable_total],
              ...(Number(q.igst) > 0
                ? [["IGST", q.igst]]
                : [["CGST", q.cgst], ["SGST", q.sgst]]),
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border py-2 text-sm">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-mono-num">{inr2(v)}</span>
              </div>
            ))}
            <div className="mt-3 flex items-center justify-between rounded bg-primary px-3 py-2.5 text-primary-foreground">
              <span className="text-xs uppercase tracking-wider">Grand total</span>
              <span className="font-mono-num text-lg font-semibold">{inr2(q.grand_total)}</span>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">{q.amount_in_words}</div>
            {q.margin_percent != null && (
              <div className="mt-4 border-t border-border pt-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cost</span>
                  <span className="font-mono-num">{inr2(q.cost_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Margin</span>
                  <span className="font-mono-num">
                    {inr2(q.margin_amount)} ({q.margin_percent}%)
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="surface p-5 text-sm">
            <div className="overline mb-2">Prepared by</div>
            <div>{q.salesperson || user?.name}</div>
            <div className="overline mt-3 mb-1">Valid until</div>
            <div>{shortDate(q.valid_until)}</div>
          </div>
        </div>
      </div>

      <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create revision</DialogTitle>
            <DialogDescription>
              A new draft version is created; the current quotation is preserved as-is.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {q.number} stays untouched. A new version (R{Number(q.revision || 0) + 1}) opens as a
            draft so you can change items and prices — nothing is lost.
          </p>
          <Field label="Reason for revision">
            <Textarea
              data-testid="revision-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer requested extra slide lane and revised price"
            />
          </Field>
          <DialogFooter>
            <Button
              data-testid="revision-create-button"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/quotations/${id}/revise`, { reason });
                  toast.success(`Revision ${data.number} created — you can edit it now`);
                  setReviseOpen(false);
                  navigate(`/quotations/${data.id}/edit`);
                } catch (e) {
                  toast.error(apiError(e));
                }
              }}
            >
              Create revision & edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
